import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  Alert,
  TextField,
  Typography,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, LockOpen, VpnKey, ArrowForward } from '@mui/icons-material';
import courseService from '../services/courseService';
import courseEnrollmentService from '../services/courseEnrollmentService';
import authService from '../services/authService';
import type { Course } from '../types';

/**
 * Browse ACTIVE courses with OPEN/CODE registration (from public discoverable API).
 * OPEN: one-click enroll. CODE: deep-link to code registration.
 */
const BrowseAvailableCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childIdParam = searchParams.get('childId');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollingId, setEnrollingId] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService.getAvailableCourses({ q: q.trim() || undefined });
      setCourses(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    void load();
  };

  const regLabel = (raw: string | undefined) => {
    const u = (raw || 'CODE').toUpperCase();
    if (u === 'OPEN') {
      return 'Open enrollment';
    }
    if (u === 'CODE') {
      return 'Code required';
    }
    return u;
  };

  const handleJoinOpen = async (courseId: number) => {
    const user = authService.getCurrentUser();
    if (!user?.id) {
      navigate('/login');
      return;
    }
    const targetUserId = childIdParam ? parseInt(childIdParam, 10) : user.id;
    if (childIdParam && Number.isNaN(targetUserId)) {
      setError('Invalid child selection');
      return;
    }
    try {
      setEnrollingId(courseId);
      const enrolledBy = user.email || 'parent';
      await courseEnrollmentService.enrollUserInCourse(targetUserId, courseId, enrolledBy);
      if (childIdParam) {
        navigate('/parent-courses');
      } else {
        navigate(`/student-courses/${courseId}`);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Enrollment failed');
    } finally {
      setEnrollingId(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Find a course
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Browse published courses you can join. Courses need at least one topic and use open or code-based
        registration.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search name or description"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
        <Button variant="outlined" onClick={() => navigate('/register-course-code')}>
          I have a code
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {courses.map((course) => {
            const method = (course.registrationMethod || 'CODE').toUpperCase();
            const isOpen = method === 'OPEN';
            return (
              <Grid item xs={12} md={6} lg={4} key={course.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {course.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={course.courseType?.replace(/_/g, ' ') || 'Course'} />
                      <Chip
                        size="small"
                        color={isOpen ? 'success' : 'default'}
                        icon={isOpen ? <LockOpen fontSize="small" /> : <VpnKey fontSize="small" />}
                        label={regLabel(course.registrationMethod)}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {course.description || '—'}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {isOpen ? (
                      <Button
                        variant="contained"
                        endIcon={enrollingId === course.id ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                        disabled={enrollingId !== null}
                        onClick={() => handleJoinOpen(course.id)}
                      >
                        Join course
                      </Button>
                    ) : (
                      <Button variant="contained" onClick={() => navigate('/register-course-code')}>
                        Enter enrollment code
                      </Button>
                    )}
                    <Button variant="text" size="small" onClick={() => navigate('/student-courses')}>
                      Back to my courses
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {!loading && courses.length === 0 && (
        <Alert severity="info">No matching courses right now. Try another search or ask your school for a code.</Alert>
      )}
    </Container>
  );
};

export default BrowseAvailableCoursesPage;
